import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { SortSelect, sortData } from '../components/SortSelect';
import { FilterPanel } from '../components/FilterPanel';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { ExportDialog } from '../components/ExportDialog';
import { useExport } from '../hooks/useExport';
import { exportTableToPDF } from '../utils/html2pdfExport';
import { HakedisDetailDialog } from '../components/modules/hakedis/HakedisDetailDialog';
import { exportHakedisDetayPDF } from '../utils/finansDetayPdf';
import * as XLSX from 'xlsx';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const HakedisPage = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [hakedisler, setHakedisler] = useState([]);
  const [odenekler, setOdenekler] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [sortKey, setSortKey] = useState('newest');
  const [submitting, setSubmitting] = useState(false);
  const [selectedHakedis, setSelectedHakedis] = useState(null);
  const [selectedRows, setSelectedRows] = useState([]);
  const { selectedColumns, setSelectedColumns, pdfPageSize, setPdfPageSize, toggleColumn, selectAll, clearAll } = useExport();
  const [exportDialogOpen, setExportDialogOpen] = useState(false);
  const [expandedOdenek, setExpandedOdenek] = useState(null);
  const [detailHakedis, setDetailHakedis] = useState(null);
  const [formData, setFormData] = useState({
    odenek_id: '',
    yil: new Date().getFullYear(),
    hakedis_no: '',
    talep_yeri: '',
    talep_tarihi: '',
    talep_yazisi_sayi_no: '',
    talep_tutari: '',
    odeme_durumu: ''
  });
  const [filters, setFilters] = useState({
    odenek_isin_adi: '',
    hakedis_no: '',
    talep_yeri: [],
    odeme_durumu: [],
    yil: []
  });

  useEffect(() => {
    if (!user?.is_admin && !user?.can_access_finance_module && !user?.is_viewer) {
      navigate('/dashboard');
      return;
    }
    fetchHakedisler();
    fetchOdenekler();
  }, []);

  const fetchHakedisler = async () => {
    try {
      // Cache-bust: her istekte unique URL (CDN/browser cache by-pass)
      const response = await axios.get(`${API}/hakedis`, {
        params: { _t: Date.now() },
        headers: { 'Cache-Control': 'no-cache', 'Pragma': 'no-cache' },
        withCredentials: true,
      });
      setHakedisler(response.data);
    } catch (error) {
      console.error('Hakedişler fetch error:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchOdenekler = async () => {
    try {
      // Cache-bust + no-cache header
      const response = await axios.get(`${API}/odenek`, {
        params: { _t: Date.now() },
        headers: { 'Cache-Control': 'no-cache', 'Pragma': 'no-cache' },
        withCredentials: true,
      });
      // Tüm ödenekleri al (hakediş herhangi bir ödenek'e bağlı olabilir)
      setOdenekler(response.data);
    } catch (error) {
      console.error('Ödenekler fetch error:', error);
    }
  };

  const addHakedisToOdenek = (odenek) => {
    setFormData({
      odenek_id: odenek.id,
      yil: odenek.yil,
      hakedis_no: '',
      talep_yeri: odenek.talep_yeri,
      talep_tarihi: '',
      talep_yazisi_sayi_no: '',
      talep_tutari: '',
      odeme_durumu: ''
    });
    setEditMode(false);
    setDialogOpen(true);
  };

  const displayMoney = (value) => {
    if (!value) return '';
    const cleaned = value.replace(/[^0-9,]/g, '');
    if (!cleaned) return '';
    const parts = cleaned.split(',');
    const formatted = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, '.');
    const result = parts[1] !== undefined ? `${formatted},${parts[1].substring(0, 2)}` : formatted;
    return result ? result + ' TL' : '';
  };


  const handleSubmit = async () => {
    if (submitting) return;
    setSubmitting(true);
    try {
      if (editMode) {
        await axios.put(`${API}/hakedis/${selectedHakedis.id}`, formData, { withCredentials: true });
        alert('Hakediş güncellendi!');
      } else {
        await axios.post(`${API}/hakedis`, formData, { withCredentials: true });
        alert('Hakediş oluşturuldu!');
      }
      setDialogOpen(false);
      resetForm();
      // Hem hakediş hem ödenek listesini yeniden çek (kullanıcı kendi girdiği veriyi anında görsün)
      await Promise.all([fetchHakedisler(), fetchOdenekler()]);
    } catch (error) {
      console.error('Submit error:', error);
      alert('Hata: ' + (error.response?.data?.detail || error.message));
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Bu hakedişi silmek istediğinizden emin misiniz?')) return;
    try {
      await axios.delete(`${API}/hakedis/${id}`, { withCredentials: true });
      alert('✅ Hakediş silindi!');
      await Promise.all([fetchHakedisler(), fetchOdenekler()]);
    } catch (error) {
      console.error('Delete error:', error);
      alert('❌ Hata: ' + error.message);
    }
  };

  const resetForm = () => {
    setFormData({
      odenek_id: '',
      yil: new Date().getFullYear(),
      hakedis_no: '',
      talep_yeri: '',
      talep_tarihi: '',
      talep_yazisi_sayi_no: '',
      talep_tutari: '',
      odeme_durumu: ''
    });
  };

  const openCreateDialog = () => {
    resetForm();
    setEditMode(false);
    setDialogOpen(true);
  };

  const openEditDialog = (hakedis) => {
    setSelectedHakedis(hakedis);
    setFormData({ ...hakedis });
    setEditMode(true);
    setDialogOpen(true);
  };

  const exportPDF = async () => {
    if (selectedColumns.length === 0) { alert('Lütfen en az bir sütun seçin'); return; }

    // Önce filtrelenmiş listeyi al
    const filteredData = hakedisler.filter(h => {
      if (filters.odenek_isin_adi && !h.odenek_isin_adi.toLowerCase().includes(filters.odenek_isin_adi.toLowerCase())) return false;
      if (filters.talep_yeri.length > 0 && !filters.talep_yeri.includes(h.talep_yeri)) return false;
      if (filters.odeme_durumu.length > 0 && !filters.odeme_durumu.includes(h.odeme_durumu)) return false;
      return true;
    });

    // Seçili satırlar varsa, filtrelenmiş listeden sadece seçilileri al
    let dataToExport;
    if (selectedRows.length > 0) {
      dataToExport = filteredData.filter(h => selectedRows.includes(h.odenek_id));
    } else {
      dataToExport = filteredData;
    }

    if (dataToExport.length === 0) { alert('Dışa aktarılacak hakediş yok'); return; }

    try {
      const columnMapping = {
        'hakedis_no': 'No', 'yil': 'Yıl', 'odenek_isin_adi': 'İş Adı',
        'talep_yeri': 'Talep', 'talep_tarihi': 'Tarih', 'talep_yazisi_sayi_no': 'Yazı',
        'talep_tutari': 'Tutar (KDV Dahil)', 'odeme_durumu': 'Durum'
      };

      const headers = ['S.NO', ...selectedColumns.map(col => columnMapping[col] || col)];
      const rows = dataToExport.map((h, index) => 
        [index + 1, ...selectedColumns.map(col => {
          if (col === 'talep_tutari') {
            const cleanValue = (h[col] || '').replace(/[^0-9,.]/g, '');
            return cleanValue ? cleanValue + ' TL' : '-';
          }
          return String(h[col] || '-');
        })]
      );

      const parasalVar = selectedColumns.includes('talep_tutari');
      let allRows = rows;
      
      if (parasalVar) {
        const toplamRow = ['TOPLAM'];
        selectedColumns.forEach(col => {
          if (col === 'talep_tutari') {
            let total = 0;
            dataToExport.forEach(h => {
              const val = parseFloat((h.talep_tutari || '').replace(/[^0-9,.]/g, '').replace(/\./g, '').replace(',', '.'));
              if (!isNaN(val)) total += val;
            });
            toplamRow.push(total.toLocaleString('tr-TR', {minimumFractionDigits: 2}) + ' TL');
          } else {
            toplamRow.push('');
          }
        });
        allRows = [...rows, toplamRow];
      }

      await exportTableToPDF(headers, allRows, 'Hakediş Listesi', `hakedis${selectedRows.length > 0 ? '-secili' : ''}.pdf`, {
        subtitle: `Kayıt: ${dataToExport.length}`,
        hasTotalRow: parasalVar,
        pageSize: pdfPageSize
      });

      setExportDialogOpen(false);
    } catch (error) {
      alert('PDF hata: ' + error.message);
    }
  };

  const exportExcel = () => {
    if (selectedColumns.length === 0) { alert('Lütfen en az bir sütun seçin'); return; }

    // Önce filtrelenmiş listeyi al
    const filteredData = hakedisler.filter(h => {
      if (filters.odenek_isin_adi && !h.odenek_isin_adi.toLowerCase().includes(filters.odenek_isin_adi.toLowerCase())) return false;
      if (filters.talep_yeri.length > 0 && !filters.talep_yeri.includes(h.talep_yeri)) return false;
      if (filters.odeme_durumu.length > 0 && !filters.odeme_durumu.includes(h.odeme_durumu)) return false;
      return true;
    });

    // Seçili satırlar varsa, filtrelenmiş listeden sadece seçilileri al
    let dataToExport;
    if (selectedRows.length > 0) {
      dataToExport = filteredData.filter(h => selectedRows.includes(h.odenek_id));
    } else {
      dataToExport = filteredData;
    }

    if (dataToExport.length === 0) { alert('Dışa aktarılacak hakediş yok'); return; }

    try {
      const columnLabels = {
        'hakedis_no': 'Hakediş No', 'yil': 'Yıl', 'odenek_isin_adi': 'İşin Adı',
        'talep_yeri': 'Talep Yeri', 'talep_tarihi': 'Talep Tarihi',
        'talep_yazisi_sayi_no': 'Talep Yazısı', 'talep_tutari': 'Tutar (KDV Dahil)',
        'odeme_durumu': 'Ödeme Durumu'
      };

      const excelData = dataToExport.map((h, index) => {
        const row = { 'S.NO': index + 1 };
        selectedColumns.forEach(col => {
          // Tutar sütunu için sadece sayısal değer
          if (col === 'talep_tutari') {
            const cleanValue = (h[col] || '').replace(/[^0-9,.]/g, '');
            row[columnLabels[col]] = cleanValue ? cleanValue + ' TL' : '';
          } else {
            row[columnLabels[col] || col] = h[col] || '';
          }
        });
        return row;
      });

      if (selectedColumns.includes('talep_tutari')) {
        const totalsRow = { 'S.NO': 'TOPLAM' };
        selectedColumns.forEach(col => {
          if (col === 'talep_tutari') {
            let total = 0;
            dataToExport.forEach(h => {
              const val = parseFloat((h.talep_tutari || '').replace(/[^0-9,.]/g, '').replace(/\./g, '').replace(',', '.'));
              if (!isNaN(val)) total += val;
            });
            totalsRow[columnLabels[col]] = total.toLocaleString('tr-TR', {minimumFractionDigits: 2}) + ' TL';
          } else {
            totalsRow[columnLabels[col] || col] = '';
          }
        });
        excelData.push(totalsRow);
      }

      const worksheet = XLSX.utils.json_to_sheet(excelData);
      worksheet['!cols'] = [{ wch: 8 }, { wch: 15 }, { wch: 40 }, { wch: 15 }, { wch: 15 }, { wch: 25 }, { wch: 18 }, { wch: 18 }];
      
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, 'Hakedişler');
      XLSX.writeFile(workbook, `hakedis${selectedRows.length > 0 ? '-secili' : ''}.xlsx`);
      setExportDialogOpen(false);
    } catch (error) {
      alert('Excel hata: ' + error.message);
    }
  };

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-950 flex items-center justify-center">
        <div className="text-white">Yükleniyor...</div>
      </div>
    );
  }

  const filteredOdenekler = odenekler.filter(odenek => {
    const odenekHakedisleri = hakedisler.filter(h => h.odenek_id === odenek.id);
    
    // İş adı filtresi
    if (filters.odenek_isin_adi && !odenek.isin_adi.toLowerCase().includes(filters.odenek_isin_adi.toLowerCase())) return false;
    
    // Yıl filtresi
    if (filters.yil.length > 0 && !filters.yil.includes(odenek.yil?.toString())) return false;
    
    // Hakediş no veya talep yeri veya ödeme durumu filtresi
    if (filters.hakedis_no || filters.talep_yeri.length > 0 || filters.odeme_durumu.length > 0) {
      const hasMatch = odenekHakedisleri.some(h => {
        if (filters.hakedis_no && !h.hakedis_no.includes(filters.hakedis_no)) return false;
        if (filters.talep_yeri.length > 0 && !filters.talep_yeri.includes(h.talep_yeri)) return false;
        if (filters.odeme_durumu.length > 0 && !filters.odeme_durumu.includes(h.odeme_durumu)) return false;
        return true;
      });
      if (!hasMatch) return false;
    }
    
    return true;
  });

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-950 via-gray-900 to-gray-950">
      <header className="bg-gray-900/50 border-b border-gray-800 backdrop-blur-sm sticky top-0 z-10">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <img src="/logo.png" alt="Kurumsal İş Yönetimi, Takip ve Koordinasyon Platformu" className="w-12 h-12 object-contain cursor-pointer" onClick={() => navigate('/dashboard')} />
              <div>
                <h1 className="text-xl font-bold text-white">Hakediş Modülü</h1>
                <p className="text-xs text-gray-400">Yatırım ve İnşaat</p>
              </div>
            </div>
            <div className="flex items-center space-x-4">
              <Button onClick={() => navigate('/')} variant="ghost" className="text-gray-300 hover:text-white" data-testid="anasayfa-nav-button">Anasayfa</Button>
              <Button onClick={() => navigate('/dashboard')} variant="ghost" className="text-gray-300 hover:text-white">Dashboard</Button>
              <Button onClick={() => navigate('/finance/ihaleler')} variant="ghost" className="text-gray-300 hover:text-white">İhaleler</Button>
              <Button onClick={() => navigate('/finance/odenek')} variant="ghost" className="text-gray-300 hover:text-white">Ödenek</Button>
              <Button onClick={() => navigate('/finance/santiye-takip')} variant="ghost" className="text-gray-300 hover:text-white">Şantiye Takip</Button>
              <Button onClick={handleLogout} variant="ghost" className="text-red-400 hover:text-red-300">Çıkış</Button>
            </div>
          </div>
        </div>
      </header>

      <main className="container mx-auto px-4 py-8">
        <div className="mb-8 flex items-center justify-between">
          <div>
            <h2 className="text-3xl font-bold text-white mb-2">📊 Hakediş</h2>
            <p className="text-gray-400">Hakediş takip sistemi</p>
          </div>
          <div className="flex gap-3">
            {hakedisler.length > 0 && (
              <Button onClick={() => {
                setExportDialogOpen(true);
                if (selectedColumns.length === 0) {
                  setSelectedColumns(['hakedis_no', 'odenek_isin_adi', 'talep_yeri', 'talep_tutari', 'odeme_durumu']);
                }
              }} className="bg-blue-600 hover:bg-blue-700 text-white">
                PDF/Excel İndir
              </Button>
            )}
          </div>
        </div>

        {/* Filtreler */}
        <FilterPanel
          filters={filters}
          setFilters={setFilters}
          totalCount={odenekler.length}
          filteredCount={filteredOdenekler.length}
          resetFilters={() => setFilters({ odenek_isin_adi: '', hakedis_no: '', talep_yeri: [], odeme_durumu: [], yil: [] })}
          filterConfig={[
            { key: 'odenek_isin_adi', label: 'İş Adı', type: 'text' },
            { key: 'hakedis_no', label: 'Hakediş No', type: 'text' },
            { key: 'yil', label: 'Yıl', type: 'multi', options: [...new Set(odenekler.map(o => o.yil?.toString()))].filter(Boolean).sort((a,b) => b-a).map(y => ({v:y,l:y})) },
            { key: 'talep_yeri', label: 'Talep Yeri', type: 'multi', options: [{v:'yigm',l:'YİGM'},{v:'spor_toto',l:'SPOR TOTO'},{v:'strateji',l:'STRATEJİ'}] },
            { key: 'odeme_durumu', label: 'Ödeme Durumu', type: 'multi', options: [{v:'geldi',l:'GELDİ'},{v:'geldi_odendi',l:'GELDİ-ÖDENDİ'},{v:'odenmedi',l:'ÖDENMEDİ'},{v:'iptal_edildi',l:'İPTAL'}] },
          ]}
        />

        {odenekler.length === 0 ? (
          <Card className="bg-gray-900 border-gray-800">
            <CardContent className="py-12 text-center">
              <p className="text-gray-400">Henüz ödenek eklenmemiş</p>
            </CardContent>
          </Card>
        ) : (
          <>
            <div className="mb-4 flex items-center justify-between gap-3 bg-gray-900/50 p-4 rounded-lg border border-gray-800">
              <div className="flex items-center gap-3">
              <input 
                type="checkbox" 
                checked={selectedRows.length === filteredOdenekler.length && filteredOdenekler.length > 0}
                onChange={(e) => {
                  if (e.target.checked) {
                    setSelectedRows(filteredOdenekler.map(o => o.id));
                  } else {
                    setSelectedRows([]);
                  }
                }}
                className="w-5 h-5" 
              />
              <span className="text-white font-medium">
                Tümünü Seç ({filteredOdenekler.length} iş)
              </span>
              </div>
              <SortSelect value={sortKey} onChange={setSortKey} options={['newest','oldest','updated','name_asc','name_desc','amount_high','amount_low']} />
            </div>
            <div className="space-y-4">
            {sortData(filteredOdenekler, sortKey, 'odenek_isin_adi', 'talep_tutari').map((odenek, index) => {
              const odenekHakedisleri = hakedisler.filter(h => h.odenek_id === odenek.id);
              const isExpanded = expandedOdenek === odenek.id;
              const isSelected = selectedRows.includes(odenek.id);
              
              return (
                <Card key={odenek.id} className={`${isSelected ? 'bg-blue-900/30 border-blue-700' : 'bg-gray-900 border-gray-800'}`}>
                  <CardHeader>
                    <div className="flex items-center gap-4">
                      <input 
                        type="checkbox" 
                        checked={isSelected}
                        onChange={(e) => {
                          e.stopPropagation();
                          if (e.target.checked) {
                            setSelectedRows([...selectedRows, odenek.id]);
                          } else {
                            setSelectedRows(selectedRows.filter(id => id !== odenek.id));
                          }
                        }}
                        className="w-5 h-5" 
                      />
                      <span className="text-gray-400 text-sm font-bold w-8">{index + 1}</span>
                      <div className="flex-1 cursor-pointer" onClick={() => setExpandedOdenek(isExpanded ? null : odenek.id)}>
                        <div className="flex items-center justify-between">
                      <div className="flex-1">
                        <CardTitle className="text-white text-lg flex items-center gap-3">
                          <svg className={`w-5 h-5 transition-transform ${isExpanded ? 'rotate-90' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                          </svg>
                          {odenek.yil} - {odenek.isin_adi}
                        </CardTitle>
                        <p className="text-sm text-gray-400 mt-1">
                          {odenekHakedisleri.length} hakediş | Toplam: {(() => {
                            let total = 0;
                            odenekHakedisleri.forEach(h => {
                              const val = parseFloat((h.talep_tutari || '').replace(/[^0-9,.]/g, '').replace(/\./g, '').replace(',', '.'));
                              if (!isNaN(val)) total += val;
                            });
                            return total.toLocaleString('tr-TR', {minimumFractionDigits: 2}) + ' TL';
                          })()}
                        </p>
                      </div>
                      <div className="flex gap-2">
                        <Button onClick={(e) => { 
                          e.stopPropagation(); 
                          // Tek ödenek PDF
                          setSelectedRows([odenek.id]);
                          setExportDialogOpen(true);
                          if (selectedColumns.length === 0) setSelectedColumns(['hakedis_no', 'talep_tutari', 'odeme_durumu']);
                        }} size="sm" className="bg-blue-600 hover:bg-blue-700 text-white">
                          📄 İndir
                        </Button>
                        {!user?.is_viewer && (
                          <Button onClick={(e) => { e.stopPropagation(); addHakedisToOdenek(odenek); }} className="bg-green-600 hover:bg-green-700 text-white">
                            + Hakediş Ekle
                          </Button>
                        )}
                      </div>
                    </div>
                    </div>
                  </div>
                  </CardHeader>
                  
                  {isExpanded && (
                    <CardContent>
                      {odenekHakedisleri.length === 0 ? (
                        <p className="text-gray-400 text-center py-4">Bu işe henüz hakediş eklenmemiş</p>
                      ) : (
                        <div className="space-y-2">
                          {odenekHakedisleri.map((h, idx) => (
                            <div key={h.id} className="bg-gray-800/50 hover:bg-gray-800/80 rounded-lg p-4 flex items-center justify-between cursor-pointer transition-colors" data-testid={`hakedis-row-${h.id}`}
                              onClick={(e) => { if (e.target.closest('button, input, a')) return; setDetailHakedis(h); }}>
                              <div className={`flex-1 grid ${user?.is_admin ? 'grid-cols-6' : 'grid-cols-5'} gap-4`}>
                                <div><span className="text-gray-500 text-xs">Hakediş:</span> <span className="text-white ml-2">{h.hakedis_no}</span></div>
                                <div><span className="text-gray-500 text-xs">Talep Yeri:</span> <span className="text-white ml-2">{h.talep_yeri.toUpperCase()}</span></div>
                                <div><span className="text-gray-500 text-xs">Tutar:</span> <span className="text-white ml-2">{displayMoney(h.talep_tutari)}</span></div>
                                <div><span className="text-gray-500 text-xs">Durum:</span> <Badge className="ml-2">{h.odeme_durumu.toUpperCase()}</Badge></div>
                                {user?.is_admin && (
                                  <div>
                                    <span className="text-gray-500 text-xs">Son Değiştiren:</span> 
                                    <span className="text-gray-400 ml-2 text-xs">{h.last_modified_by_name || '-'}</span>
                                    {h.last_modified_at && (
                                      <div className="text-gray-500 text-xs mt-1">
                                        {new Date(h.last_modified_at).toLocaleString('tr-TR')}
                                      </div>
                                    )}
                                  </div>
                                )}
                                <div className="flex gap-2 justify-end">
                                  <Button onClick={() => openEditDialog(h)} size="sm" variant="outline" className="bg-gray-700 text-white hover:bg-gray-600">Düzenle</Button>
                                  {(user?.is_admin || user?.is_admin_assistant) && (
                                    <Button onClick={() => handleDelete(h.id)} size="sm" variant="outline" className="bg-red-900/20 border-red-700 text-red-400">Sil</Button>
                                  )}
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </CardContent>
                  )}
                </Card>
              );
            })}
          </div>
          </>
        )}

        {selectedRows.length > 0 && (
          <Card className="mt-6 bg-gradient-to-br from-blue-900/30 to-purple-900/30 border-blue-700/50">
            <CardHeader><CardTitle className="text-blue-400">📊 Seçili Hakedişler Toplamı ({selectedRows.length})</CardTitle></CardHeader>
            <CardContent>
              <div>
                <p className="text-xs text-gray-400 mb-1">Talep Tutarı Toplamı:</p>
                <p className="text-2xl font-bold text-blue-300">
                  {(() => {
                    let total = 0;
                    // Seçili ödeneklerin tüm hakedişlerini topla
                    hakedisler.filter(h => selectedRows.includes(h.odenek_id)).forEach(h => {
                      const val = parseFloat((h.talep_tutari || '').replace(/[^0-9,.]/g, '').replace(/\./g, '').replace(',', '.'));
                      if (!isNaN(val)) total += val;
                    });
                    return total.toLocaleString('tr-TR', {minimumFractionDigits: 2}) + ' TL';
                  })()}
                </p>
              </div>
              <div className="mt-4 flex gap-3">
                <Button onClick={() => { setExportDialogOpen(true); if (selectedColumns.length === 0) setSelectedColumns(['hakedis_no', 'odenek_isin_adi', 'talep_tutari', 'odeme_durumu']); }} className="bg-purple-600 hover:bg-purple-700 text-white">Seçilileri İndir</Button>
                <Button onClick={() => setSelectedRows([])} variant="outline" className="bg-gray-800 border-gray-700 text-white hover:bg-gray-700">Seçimi Temizle</Button>
              </div>
            </CardContent>
          </Card>
        )}
      </main>

      {/* Form Dialog */}
      <HakedisDetailDialog
        open={!!detailHakedis}
        onOpenChange={(v) => { if (!v) setDetailHakedis(null); }}
        hakedis={detailHakedis}
        odenek={detailHakedis ? odenekler.find(o => o.id === detailHakedis.odenek_id) : null}
        hakedisler={hakedisler}
        displayMoney={displayMoney}
        canEdit={!user?.is_viewer}
        onEdit={(h) => openEditDialog(h)}
        onExportPDF={(h) => exportHakedisDetayPDF(h, { displayMoney, odenek: odenekler.find(o => o.id === h.odenek_id), hakedisler })}
      />

      <Dialog open={dialogOpen} onOpenChange={(open) => { if (!open && dialogOpen) return; setDialogOpen(open); }}>
        <DialogContent className="bg-gray-900 border-gray-800 text-white max-w-3xl">
          <DialogHeader>
            <DialogTitle>{editMode ? 'Hakediş Düzenle' : 'Yeni Hakediş Ekle'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Ödenek/İş Seç *</Label>
              <Select value={formData.odenek_id} onValueChange={(value) => setFormData({...formData, odenek_id: value})}>
                <SelectTrigger className="bg-gray-800 border-gray-700 text-white">
                  <SelectValue placeholder="Ödenek seçin (sadece GELDİ olanlar)" />
                </SelectTrigger>
                <SelectContent className="bg-gray-800 border-gray-700 text-white max-h-60">
                  {odenekler.length === 0 ? (
                    <SelectItem value="none" disabled>GELDİ ödenek yok</SelectItem>
                  ) : (
                    odenekler.map(o => (
                      <SelectItem key={o.id} value={o.id}>{o.yil} - {o.isin_adi}</SelectItem>
                    ))
                  )}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Yıl *</Label>
                <Input type="number" value={formData.yil} onChange={(e) => setFormData({...formData, yil: parseInt(e.target.value)})} className="bg-gray-800 border-gray-700 text-white" required />
              </div>
              <div className="space-y-2">
                <Label>Hakediş No *</Label>
                <Input value={formData.hakedis_no} onChange={(e) => setFormData({...formData, hakedis_no: e.target.value})} className="bg-gray-800 border-gray-700 text-white" placeholder="1, 2, 3..." required />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Talep Yeri *</Label>
                <Select value={formData.talep_yeri} onValueChange={(value) => setFormData({...formData, talep_yeri: value})}>
                  <SelectTrigger className="bg-gray-800 border-gray-700 text-white"><SelectValue placeholder="Seçin" /></SelectTrigger>
                  <SelectContent className="bg-gray-800 border-gray-700 text-white">
                    <SelectItem value="yigm">YİGM</SelectItem>
                    <SelectItem value="spor_toto">SPOR TOTO</SelectItem>
                    <SelectItem value="strateji">STRATEJİ</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Talep Tarihi</Label>
                <Input type="date" value={formData.talep_tarihi} onChange={(e) => setFormData({...formData, talep_tarihi: e.target.value})} className="bg-gray-800 border-gray-700 text-white" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Talep Yazısı Sayı/No</Label>
                <Input value={formData.talep_yazisi_sayi_no} onChange={(e) => setFormData({...formData, talep_yazisi_sayi_no: e.target.value})} className="bg-gray-800 border-gray-700 text-white" />
              </div>
              <div className="space-y-2">
                <Label>Talep Tutarı (KDV Dahil)</Label>
                <Input value={displayMoney(formData.talep_tutari)} onChange={(e) => setFormData({...formData, talep_tutari: e.target.value.replace(/ TL/g, '')})} className="bg-gray-800 border-gray-700 text-white" placeholder="3600848,45" />
              </div>
            </div>
            <div className="space-y-2">
              <Label>Ödeme Durumu *</Label>
              <Select value={formData.odeme_durumu} onValueChange={(value) => setFormData({...formData, odeme_durumu: value})}>
                <SelectTrigger className="bg-gray-800 border-gray-700 text-white"><SelectValue placeholder="Seçin" /></SelectTrigger>
                <SelectContent className="bg-gray-800 border-gray-700 text-white">
                  <SelectItem value="geldi">GELDİ</SelectItem>
                  <SelectItem value="geldi_odendi">GELDİ-ÖDENDİ</SelectItem>
                  <SelectItem value="odenmedi">ÖDENMEDİ</SelectItem>
                  <SelectItem value="iptal_edildi">İPTAL EDİLDİ</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button onClick={() => { setDialogOpen(false); resetForm(); }} variant="ghost" className="text-gray-400">İptal</Button>
            <Button onClick={handleSubmit} disabled={submitting} className="bg-green-600 hover:bg-green-700 text-white">{submitting ? 'Kaydediliyor...' : (editMode ? 'Güncelle' : 'Oluştur')}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Export Dialog */}
      <ExportDialog
        open={exportDialogOpen}
        onOpenChange={setExportDialogOpen}
        columns={[
          {id: 'hakedis_no', label: 'Hakediş No'},
          {id: 'yil', label: 'Yıl'},
          {id: 'odenek_isin_adi', label: 'İşin Adı'},
          {id: 'talep_yeri', label: 'Talep Yeri'},
          {id: 'talep_tarihi', label: 'Talep Tarihi'},
          {id: 'talep_yazisi_sayi_no', label: 'Talep Yazısı'},
          {id: 'talep_tutari', label: 'Talep Tutarı'},
          {id: 'odeme_durumu', label: 'Ödeme Durumu'}
        ]}
        selectedColumns={selectedColumns}
        onToggleColumn={toggleColumn}
        onSelectAll={() => selectAll([
          {id: 'hakedis_no'}, {id: 'yil'}, {id: 'odenek_isin_adi'}, {id: 'talep_yeri'},
          {id: 'talep_tarihi'}, {id: 'talep_yazisi_sayi_no'}, {id: 'talep_tutari'}, {id: 'odeme_durumu'}
        ])}
        onClearAll={clearAll}
        pdfPageSize={pdfPageSize}
        onPdfPageSizeChange={setPdfPageSize}
        onExportPDF={exportPDF}
        onExportExcel={exportExcel}
        gridCols={2}
        idPrefix="hakedis-export"
      />
    </div>
  );
};

export default HakedisPage;